/**
 * Buy membership (DNKADM-0222) — the membership-plan order dialog: pick a plan, then
 * comp it or charge a card. Amounts are the SERVER's throughout (the quote prices the
 * plan and the receipt states what was charged); nothing here computes money.
 *
 * **Selling to a card on file (DNKADM-0253).** In card mode the dialog offers the
 * member's saved cards through the SHARED {@link CardOnFileChooser} — the same component
 * {@link SellCartDialog} uses. Picking one hides the `CardPaymentField` (there is nothing
 * to tokenize) and the order carries `payment.cardId`; the credentials are mutually
 * exclusive in the type, so a body can never carry both or neither. "Use a new card"
 * tokenizes and then **saves the card first** (`saveMemberCard`, bound to the working
 * `locationId`), ordering with the id that comes back — so a card taken at the counter is
 * on file next time. A failed save issues NO order and never falls back to a token-only
 * charge.
 *
 * **The two connection `422`s are membership-only.** A saved card belongs to the payment
 * connection it was saved against, and only a MEMBERSHIP sale can be rejected for it:
 * `SAVED_CARD_CONNECTION_MISMATCH` (saved at another location) and
 * `SAVED_CARD_CONNECTION_UNAVAILABLE` (that connection is gone). Both are branched on the
 * **envelope code**, never on message text; both show the server's own message; and
 * neither offers a plain Retry, because re-sending the same `cardId` cannot succeed. The
 * way forward is the "Take the card again" action, which drops back to new-card entry
 * with a FRESH idempotency key — the member keeps both cards and the sale proceeds. The
 * plan selection and the dialog stay exactly as they were.
 */
import { formatApiDate } from '@/components/list/listDate';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isNeedsReview, unsettledOrderMessage } from '@/domain/orderStatus';
import { OrderStatusRow } from '@/components/OrderStatusBadge';
import { IconAction } from '@/components/IconAction';
import { useManagerPinInputProps } from '@/components/useManagerPinInputProps';
import { ApiError } from '@/api/errors';
import { saveMemberCard } from '@/api/memberCards';
import { listMembershipPlans, type MembershipPlan } from '@/api/membershipPlans';
import {
  createMemberOrder,
  getMemberOrder,
  getMembershipQuote,
  refundMemberOrder,
  type MembershipOrder,
  type MembershipQuote,
  type OpenOrderNeedsAttention,
  voidMemberOrder,
} from '@/api/membershipOrders';
import { CardTokenizationError, newIdempotencyKey } from '@/api/payments';
import { serverMessage } from '@/api/serverMessage';
import { declineReasonLineFromError } from '@/utils/declineReason';
import CardPaymentField, { type TokenizeFn } from '@/components/payments/CardPaymentField';
import { useCurrentOperator, useStaffTier } from '@/auth/authHooks';
import { useServiceLocation } from '@/location/useServiceLocation';
import { Icon, inkAlpha, MONO, resolveTokens } from '@/theme';
// The client-action driver seam lives in `./paymentActionDriver` so the sell cart drives
// the SAME registered driver (DNKADM-0245). Its test setter is imported from there
// directly; the compatibility re-export that used to sit here was dropped in
// DNKADM-0249 (a non-component export beside a component trips react-refresh).
import { getPaymentActionDriver, UnsupportedPaymentActionError } from './paymentActionDriver';
import { CardOnFileChooser } from './CardOnFileChooser';
import {
  compOverrideAuth,
  compPinAllowsSubmit,
  compPinErrorView,
  resolveCompPinGate,
  compPinRequired,
  compPinShown,
  compChoiceShown,
  type CompPinGate,
} from './compPin';
import { NegotiatedPricePinPrompt } from './NegotiatedPricePinPrompt';
import {
  canonicalCurrencyCode,
  majorPriceInput,
  negotiatedPriceErrorView,
  negotiatedPriceToMinor,
  useNegotiatedPriceOverride,
} from './negotiatedPriceOverride';
import {
  CHECK_AGAIN_FAILED,
  outcomeIsUnknown,
  unconfirmedClosePrompt,
  unconfirmedMessage,
} from './unconfirmedOutcome';
import { OrderNeedsAttentionAlert } from './OrderNeedsAttentionAlert';
import { withOpenOrder } from './orderNeedsAttention';

const COMP_TERMS = [1, 3, 6, 12] as const;
const ORDER_POLL_INTERVAL_MS = 1500;
const ORDER_MAX_POLLS = 20;

const NEGOTIATED_MEMBERSHIP_COPY =
  "This negotiated rate applies only to this member. The plan's list price and every other member remain unchanged. It recurs on every renewal, not just today's charge, until this member's plan is changed. To undo it, use Change plan for this member.";

/**
 * The two `422` envelope codes a saved-card MEMBERSHIP sale can be rejected with
 * (DNKADM-0253). A one-time sale (credits/packages) cannot hit either — the charge runs
 * on the card's own connection — so the cart deliberately does not handle them.
 * Branching is on these codes ALONE; message text is never parsed.
 */
const SAVED_CARD_CONNECTION_CODES = {
  /** The card was saved against a different location than the plan resolves to. */
  mismatch: 'SAVED_CARD_CONNECTION_MISMATCH',
  /** The card's payment connection is no longer active; the card must be re-entered. */
  unavailable: 'SAVED_CARD_CONNECTION_UNAVAILABLE',
} as const;

interface BuyMembershipOrderDialogProps {
  contactId: string;
  open: boolean;
  onClose: () => void;
  onCompleted: (order: MembershipOrder) => void;
  onReversed?: ((order: MembershipOrder) => void) | undefined;
  /**
   * The plan the caller pre-flighted (DNKADM-0231). When set, the dialog opens
   * **locked** to that plan — read-only, no picker — so the ordered plan is always the
   * gated plan. Absent (defensive/legacy): the free picker, as before.
   */
  boundPlanId?: string | undefined;
}

type PlansState =
  | { status: 'idle' | 'loading'; plans: MembershipPlan[] }
  | { status: 'ready'; plans: MembershipPlan[] }
  | { status: 'error'; plans: MembershipPlan[]; message: string };

type SubmitError = {
  message: string;
  retryable: boolean;
  /**
   * The only way forward is a NEW card (DNKADM-0253) — set on the two saved-card
   * connection `422`s. It replaces the Retry action rather than joining it: re-sending
   * the same `cardId` cannot succeed, so a plain retry must never be offered.
   */
  recovery?: 'new-card';
  /**
   * DNKADM-0475 (F-99): set on a `409 ORDER_NEEDS_ATTENTION` — an earlier card order of this
   * member is still open, so the alert names it and links to Payments instead of a charge.
   */
  openOrder?: OpenOrderNeedsAttention;
};

/**
 * DNKADM-0467: an attempt the server never confirmed either way. Its idempotency key is
 * kept and the plan, terms and mode stay locked until "Check again" settles it under
 * that key.
 */
type Unconfirmed = {
  message: string;
  /** The order the attempt produced, once known: "Check again" then reads it directly. */
  orderId: number | null;
  /** The saved card the attempt charged, so "Check again" re-sends the same credential. */
  cardId: string | null;
  /** Set when "Check again" could not read the order; the attempt stays locked. */
  readFailed?: boolean;
  /**
   * DNKADM-0475 (F-100): locked on a `NEEDS_REVIEW` order — the card was charged and nothing
   * applied — so the close prompt says so instead of "couldn't confirm".
   */
  needsReview?: boolean;
};

type PaymentMode = 'comp' | 'card';

/** Defer = $0 today, first charge on startOn; Pay = prorated charge today. */
type BillingStartChoice = 'defer' | 'pay';

type MembershipRateLine = {
  type: 'membership_plan';
  refId: number;
  overrideBase?: number;
};

type QuoteState =
  | { status: 'idle' | 'loading' }
  | { status: 'blocked' | 'awaiting-override-pin' }
  | {
      status: 'ready';
      quote: MembershipQuote;
      requestLine: MembershipRateLine;
      requestAuth: { pin: string } | null;
    }
  | { status: 'error'; message: string; retryable: boolean };

export function BuyMembershipOrderDialog({
  contactId,
  open,
  onClose,
  onCompleted,
  onReversed,
  boundPlanId,
}: BuyMembershipOrderDialogProps) {
  if (!open) return null;
  return (
    <BuyMembershipOrderDialogBody
      key={contactId}
      contactId={contactId}
      onClose={onClose}
      onCompleted={onCompleted}
      onReversed={onReversed}
      boundPlanId={boundPlanId}
    />
  );
}

function BuyMembershipOrderDialogBody({
  contactId,
  onClose,
  onCompleted,
  onReversed,
  boundPlanId: boundPlanIdProp,
}: {
  contactId: string;
  onClose: () => void;
  onCompleted: (order: MembershipOrder) => void;
  onReversed?: ((order: MembershipOrder) => void) | undefined;
  boundPlanId?: string | undefined;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const titleId = `${ids}-title`;
  const planId = `${ids}-plan`;
  const termId = `${ids}-term`;
  const reasonId = `${ids}-reason`;
  const location = useServiceLocation();
  const operator = useCurrentOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';

  // The pre-flighted plan (DNKADM-0231). Non-empty ⇒ the dialog is locked to it.
  const boundPlanId =
    boundPlanIdProp !== undefined && boundPlanIdProp.length > 0 ? boundPlanIdProp : null;

  const [plansState, setPlansState] = useState<PlansState>({ status: 'idle', plans: [] });
  const [selectedPlanId, setSelectedPlanId] = useState(boundPlanId ?? '');
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('card');
  const [termMonths, setTermMonths] = useState<number>(3);
  const [reason, setReason] = useState('');
  const isStaffTier = useStaffTier();
  const [compPin, setCompPin] = useState('');
  const [compPinGate, setCompPinGate] = useState<CompPinGate>({ status: 'idle' });
  /** The server's refusal of the last comp PIN, shown on the field. */
  const [compPinError, setCompPinError] = useState<string | null>(null);
  /** A PIN refusal keeps the field on screen even where the connection read said no PIN. */
  const [compPinAsked, setCompPinAsked] = useState(false);
  /** Presence means the operator edited the list rate; absence preserves list price. */
  const [membershipRateEdit, setMembershipRateEdit] = useState<string | undefined>(undefined);
  const [quoteState, setQuoteState] = useState<QuoteState>({ status: 'idle' });
  /**
   * DNKADM-0374: defer or pay-through for anchored card orders. DNKADM-0380 U1: the default is
   * decided when the quote lands (Pay through when the anchor is available with a prorate), and
   * only while the operator has not touched the control — see `billingStartTouchedRef`.
   */
  const [billingStart, setBillingStart] = useState<BillingStartChoice>('defer');
  /** Set the moment the operator picks an option; from then on a quote refetch never moves it. */
  const billingStartTouchedRef = useRef(false);
  /** Established only by a successful list-price quote; never defaulted. */
  const [authoritativeCurrency, setAuthoritativeCurrency] = useState<string | null>(null);
  const authoritativeCurrencyRef = useRef<string | null>(null);
  const [cardReady, setCardReady] = useState(false);
  /** The chosen card on file, or `null` while the admin is entering a new one. */
  const [cardOnFileId, setCardOnFileId] = useState<string | null>(null);
  const [paymentNote, setPaymentNote] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [receipt, setReceipt] = useState<MembershipOrder | null>(null);
  const [reversing, setReversing] = useState<'refund' | 'void' | null>(null);
  const [reversalError, setReversalError] = useState<string | null>(null);
  const [reversalMessage, setReversalMessage] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<Unconfirmed | null>(null);
  const attemptKeyRef = useRef<string | null>(null);
  const tokenizeRef = useRef<TokenizeFn | null>(null);
  const quoteSeqRef = useRef(0);

  const storeAuthoritativeCurrency = useCallback((currency: string | null) => {
    authoritativeCurrencyRef.current = currency;
    setAuthoritativeCurrency(currency);
  }, []);

  const loadPlans = useCallback(() => {
    setPlansState((prev) => ({ status: 'loading', plans: prev.plans }));
    void listMembershipPlans({ status: 'active' })
      .then((plans) => setPlansState({ status: 'ready', plans }))
      .catch((error: unknown) =>
        setPlansState({
          status: 'error',
          plans: [],
          message: serverMessage(error) ?? 'Membership plans could not be loaded.',
        }),
      );
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const selectedPlan = plansState.plans.find((plan) => plan.id === selectedPlanId) ?? null;
  const selectedPlanNumber = Number(selectedPlanId);
  const locationId = location.selectedId !== null ? Number(location.selectedId) : Number.NaN;
  const planValid =
    selectedPlanId.length > 0 && Number.isFinite(selectedPlanNumber) && selectedPlanNumber > 0;
  // A bound plan must resolve against the loaded active plans before anything is quoted or
  // ordered; a stale bound plan is surfaced, NEVER downgraded to a free picker (that would
  // reopen the gate bypass this binding closes).
  const boundPlanReady = boundPlanId === null || selectedPlan !== null;
  const boundPlanMissing =
    boundPlanId !== null && plansState.status === 'ready' && selectedPlan === null;
  const termValid = Number.isInteger(termMonths) && termMonths > 0;
  const locationReady = location.status === 'ready' && Number.isFinite(locationId);
  const tenantReady = tenantSlug.length > 0;
  const negotiatedRateMinor =
    paymentMode === 'card' &&
    membershipRateEdit !== undefined &&
    authoritativeCurrency !== null
      ? negotiatedPriceToMinor(membershipRateEdit, authoritativeCurrency)
      : null;
  const negotiatedRateInvalid =
    paymentMode === 'card' &&
    membershipRateEdit !== undefined &&
    authoritativeCurrency !== null &&
    negotiatedRateMinor === null;
  const hasNegotiatedRate = negotiatedRateMinor !== null;
  const negotiatedPrice = useNegotiatedPriceOverride(hasNegotiatedRate);
  const overrideAuth = negotiatedPrice.overrideAuth;
  const invalidateNegotiatedPrice = negotiatedPrice.invalidate;
  const readyQuoteMatchesCurrent =
    quoteState.status === 'ready' &&
    quoteRequestMatches(
      quoteState,
      selectedPlanNumber,
      negotiatedRateMinor,
      overrideAuth ?? null,
    );
  // DNKADM-0400: the server checks the comp PIN; the console only decides whether to ask.
  const compPinFieldRequired = compPinRequired(compPinGate);
  const compPinFieldShown = compPinAsked || compPinShown(compPinGate);
  const compGateAllowsSubmit = compPinAllowsSubmit(compPinGate, compPin);
  const compReadyToSubmit = termValid && compGateAllowsSubmit;
  // A card on file needs no tokenizer — there is nothing left to enter (DNKADM-0253).
  const cardReadyToSubmit =
    paymentMode === 'card' &&
    quoteState.status === 'ready' &&
    tenantReady &&
    (cardOnFileId !== null || (cardReady && tokenizeRef.current !== null));
  const canSubmit =
    plansState.status === 'ready' &&
    planValid &&
    boundPlanReady &&
    (paymentMode === 'comp' ||
      (authoritativeCurrency !== null && readyQuoteMatchesCurrent)) &&
    (paymentMode === 'comp' ? compReadyToSubmit : cardReadyToSubmit) &&
    locationReady &&
    !submitting &&
    unconfirmed === null &&
    receipt === null;
  // In flight, or an attempt whose outcome is unconfirmed (DNKADM-0467): no edit may start
  // a new attempt while the last one may still land.
  const locked = submitting || unconfirmed !== null;

  function clearAttemptForEdit() {
    attemptKeyRef.current = null;
    setSubmitError(null);
    setReceipt(null);
    setPaymentNote(null);
    setReversalError(null);
    setReversalMessage(null);
    setCompPin('');
    setCardReady(false);
    setCardOnFileId(null);
    tokenizeRef.current = null;
    if (paymentMode === 'card') setQuoteState({ status: 'idle' });
  }

  /** A rate/PIN edit invalidates pricing without discarding the selected card. */
  function clearNegotiatedAttemptForEdit() {
    attemptKeyRef.current = null;
    setSubmitError(null);
    setReceipt(null);
    setPaymentNote(null);
    setReversalError(null);
    setReversalMessage(null);
    if (paymentMode === 'card') setQuoteState({ status: 'idle' });
  }

  /**
   * Choosing a different credential is a new sale ATTEMPT, not a new plan: the attempt
   * key and any stale error go, and a card entry in progress is dropped. Deliberately
   * NOT {@link clearAttemptForEdit} — that invalidates the pricing quote, which is about
   * the plan and has not changed.
   */
  function selectCardOnFile(next: string | null) {
    attemptKeyRef.current = null;
    setSubmitError(null);
    setPaymentNote(null);
    setCardOnFileId(next);
    if (next !== null) {
      tokenizeRef.current = null;
      setCardReady(false);
    }
  }

  const loadQuote = useCallback(() => {
    if (paymentMode !== 'card' || !planValid || !boundPlanReady) {
      quoteSeqRef.current += 1;
      setQuoteState({ status: 'idle' });
      return;
    }
    if (negotiatedRateInvalid) {
      quoteSeqRef.current += 1;
      setQuoteState({ status: 'blocked' });
      return;
    }
    if (hasNegotiatedRate && overrideAuth === undefined) {
      quoteSeqRef.current += 1;
      setQuoteState({ status: 'awaiting-override-pin' });
      return;
    }
    const seq = quoteSeqRef.current + 1;
    quoteSeqRef.current = seq;
    setQuoteState({ status: 'loading' });
    const line =
      negotiatedRateMinor === null
        ? { type: 'membership_plan' as const, refId: selectedPlanNumber }
        : {
            type: 'membership_plan' as const,
            refId: selectedPlanNumber,
            overrideBase: negotiatedRateMinor,
          };
    const requestAuth = overrideAuth ?? null;
    // DNKADM-0374: pass payment locationId so the server can resolve a billing anchor.
    const quoteLocationId = Number.isFinite(locationId) ? locationId : undefined;
    void getMembershipQuote(contactId, [line], quoteLocationId, requestAuth ?? undefined)
      .then((quote) => {
        if (quoteSeqRef.current !== seq) return;
        const responseCurrency = canonicalCurrencyCode(quote.currency);
        if (responseCurrency === null) {
          setQuoteState({
            status: 'error',
            message: 'Pricing currency is unavailable. Retry before setting a membership rate.',
            retryable: true,
          });
          return;
        }
        if (
          authoritativeCurrencyRef.current !== null &&
          responseCurrency !== authoritativeCurrencyRef.current
        ) {
          // Re-establish a list-price baseline before another override can be applied.
          storeAuthoritativeCurrency(null);
          invalidateNegotiatedPrice();
          setQuoteState({
            status: 'error',
            message: 'Pricing currency changed. Re-price the membership before continuing.',
            retryable: true,
          });
          return;
        }
        if (authoritativeCurrencyRef.current === null) {
          storeAuthoritativeCurrency(responseCurrency);
        }
        // DNKADM-0380 U1: preselect Pay through when the anchor is available WITH a prorate; Defer
        // otherwise (anchor unavailable, below-floor defer-only, or the unanchored path). Applied only
        // while the operator has not touched the control — after that, a refetch (a rate edit, a plan
        // change) never moves their selection, except to fall back to Defer when the new quote no
        // longer offers Pay through at all.
        const payThroughOffered =
          quote.billingAnchor?.available === true && quote.billingAnchor.prorate !== undefined;
        if (!billingStartTouchedRef.current) {
          setBillingStart(payThroughOffered ? 'pay' : 'defer');
        } else if (!payThroughOffered) {
          setBillingStart('defer');
        }
        setQuoteState({ status: 'ready', quote, requestLine: line, requestAuth });
      })
      .catch((error: unknown) => {
        if (quoteSeqRef.current !== seq) return;
        const view = toOrderErrorView(error);
        setQuoteState({
          status: 'error',
          message: view.message,
          retryable: view.retryable,
        });
      });
  }, [
    boundPlanReady,
    contactId,
    hasNegotiatedRate,
    negotiatedRateInvalid,
    negotiatedRateMinor,
    invalidateNegotiatedPrice,
    locationId,
    overrideAuth,
    paymentMode,
    planValid,
    selectedPlanNumber,
    storeAuthoritativeCurrency,
  ]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    setCompPinError(null);
    setCompPinAsked(false);
    if (!locationReady) {
      setCompPinGate({ status: 'idle' });
      setCompPin('');
      return;
    }
    let cancelled = false;
    setCompPin('');
    setCompPinGate({ status: 'loading' });
    void resolveCompPinGate(isStaffTier, location.selectedId ?? undefined).then((gate) => {
      if (cancelled) return;
      setCompPinGate(gate);
      // DNKADM-0476 F-62: where the Comp choice is not offered, a comp in progress falls back to card.
      if (!compChoiceShown(gate, isStaffTier)) setPaymentMode((mode) => (mode === 'comp' ? 'card' : mode));
    });
    return () => {
      cancelled = true;
    };
  }, [isStaffTier, location.selectedId, locationReady]);

  function close() {
    if (submitting || reversing !== null) return;
    // DNKADM-0467: an unconfirmed attempt may still land, and a sale started after closing
    // would carry a new key — so closing is confirmed, never silently allowed.
    if (unconfirmed !== null && !window.confirm(unconfirmedClosePrompt(unconfirmed.needsReview))) return;
    onClose();
  }

  async function submit() {
    setSubmitError(null);
    setPaymentNote(null);
    if (!planValid) {
      setSubmitError({ retryable: false, message: 'Pick a membership plan.' });
      return;
    }
    if (paymentMode === 'comp' && !termValid) {
      setSubmitError({ retryable: false, message: 'Pick a positive comp term.' });
      return;
    }
    if (paymentMode === 'comp' && !compGateAllowsSubmit) {
      return;
    }
    if (!locationReady) {
      setSubmitError({ retryable: false, message: locationMessage(location.status) });
      return;
    }
    if (hasNegotiatedRate && overrideAuth === undefined) {
      setSubmitError({
        retryable: false,
        message: 'Apply the negotiated rate before continuing.',
      });
      return;
    }

    if (paymentMode === 'card') {
      await submitCardOrder();
      return;
    }

    await submitCompOrder();
  }

  async function reverseReceipt(action: 'refund' | 'void') {
    const current = receipt;
    if (current === null || current.status !== 'COMPLETED' || current.orderId === null) return;
    const label = current.orderNumber ?? current.orderId;
    const verb = action === 'refund' ? 'Refund' : 'Void';
    if (!window.confirm(`${verb} order ${label}? This cannot be undone.`)) return;

    setReversing(action);
    setSubmitError(null);
    setReversalError(null);
    setReversalMessage(null);
    try {
      const changed =
        action === 'refund'
          ? await refundMemberOrder(contactId, current.orderId)
          : await voidMemberOrder(contactId, current.orderId);
      const reread = await getMemberOrder(contactId, changed.orderId ?? current.orderId);
      setReceipt(reread);
      setReversalMessage(reversalSuccessMessage(reread, action));
      onReversed?.(reread);
    } catch (error) {
      setReversalError(
        toOrderErrorView(
          error,
          `The order could not be ${action === 'refund' ? 'refunded' : 'voided'}.`,
        ).message,
      );
    } finally {
      setReversing(null);
    }
  }

  async function submitCompOrder() {
    const idempotencyKey = attemptKeyRef.current ?? crypto.randomUUID();
    attemptKeyRef.current = idempotencyKey;
    const trimmedReason = reason.trim();
    setSubmitting(true);
    try {
      const payment: {
        mode: 'comp';
        locationId: number;
        idempotencyKey: string;
        reason?: string;
      } = { mode: 'comp', locationId, idempotencyKey };
      if (trimmedReason.length > 0) payment.reason = trimmedReason;
      const pinAuth = compOverrideAuth(compPin);
      const order = await createMemberOrder(contactId, {
        lines: [{ type: 'membership_plan', refId: selectedPlanNumber, compTermMonths: termMonths }],
        payment,
        ...(pinAuth === undefined ? {} : { overrideAuth: pinAuth }),
      });
      finishOrder(order, null);
    } catch (error) {
      const pinView = compPinErrorView(error);
      if (pinView !== null) {
        // The server refused the PIN before doing anything: a fresh attempt next time.
        // Not after an unconfirmed send of this key, though — that send may still land,
        // so the key and the lock stay, and a PIN can be entered for "Check again".
        if (unconfirmed === null) attemptKeyRef.current = null;
        setPaymentNote(null);
        setCompPinAsked(true);
        setCompPinError(pinView.message);
        return;
      }
      setPaymentNote(null);
      failAttempt(error, null);
    } finally {
      // The PIN is never kept after a submit, whatever the outcome.
      setCompPin('');
      setSubmitting(false);
    }
  }

  async function submitCardOrder() {
    if (!tenantReady) {
      setSubmitError({
        retryable: false,
        message: 'Tenant payment context is unavailable. Sign in again before charging a card.',
      });
      return;
    }
    if (quoteState.status !== 'ready' || !readyQuoteMatchesCurrent) {
      setSubmitError({ retryable: true, message: 'Pricing quote must load before charging.' });
      return;
    }
    if (cardOnFileId === null && (!cardReady || tokenizeRef.current === null)) {
      setSubmitError({ retryable: false, message: 'Finish entering the card before charging.' });
      return;
    }

    setSubmitting(true);
    // Whichever way the card arrived, the order names a card ON FILE: a new card is
    // SAVED FIRST, so the counter card is there next time. A failed save ends the
    // attempt here — no order is issued and there is no token-only fallback.
    let cardId = cardOnFileId;
    if (cardId === null) {
      try {
        cardId = await saveEnteredCard();
      } catch (error) {
        setSubmitError(
          toOrderErrorView(error, 'The card could not be saved, so nothing was charged.'),
        );
        setSubmitting(false);
        return;
      }
    }
    await placeCardOrder(cardId);
  }

  /**
   * Charge the attempt's saved card. One key per ATTEMPT, reused on a retry and on
   * "Check again": the server answers a key it has seen with the order that key already
   * produced, and claims the order before it charges, so the card is charged at most once.
   */
  async function placeCardOrder(cardId: string) {
    if (quoteState.status !== 'ready') {
      setSubmitting(false);
      return;
    }
    const pricedLine = quoteState.requestLine;
    const pricedAuth = quoteState.requestAuth;
    setSubmitting(true);
    try {
      const idempotencyKey = attemptKeyRef.current ?? newIdempotencyKey();
      attemptKeyRef.current = idempotencyKey;
      // DNKADM-0374: pay-through = send proratePartialPeriod: true; defer = omit.
      const billingAnchor =
        quoteState.status === 'ready' ? quoteState.quote.billingAnchor : undefined;
      const isPayThrough =
        billingStart === 'pay' && billingAnchor?.available === true;
      const order = await createMemberOrder(contactId, {
        lines: [pricedLine],
        payment: {
          mode: 'card',
          cardId,
          locationId,
          idempotencyKey,
        },
        ...(pricedAuth === null ? {} : { overrideAuth: pricedAuth }),
        ...(isPayThrough ? { proratePartialPeriod: true } : {}),
      });
      await settleCardOrder(order, cardId);
    } catch (error) {
      failAttempt(error, cardId);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * A failed order request. DNKADM-0467: when the server may have committed the order
   * the key is kept and the dialog locks until "Check again"; only a definitive refusal
   * ends the attempt (and, DNKADM-0458, the negotiated-price PIN with it).
   */
  function failAttempt(error: unknown, cardId: string | null) {
    if (outcomeIsUnknown(error)) {
      setUnconfirmed({
        message: unconfirmedMessage(cardId === null ? 'comp' : 'card'),
        orderId: unconfirmed?.orderId ?? null,
        cardId,
      });
      return;
    }
    attemptKeyRef.current = null;
    setUnconfirmed(null);
    if (cardId !== null && overrideAuth !== undefined) negotiatedPrice.reset();
    setSubmitError(withOpenOrder(error, toOrderErrorView(error)));
  }

  /**
   * DNKADM-0467: settle an unconfirmed attempt under its OWN key before anything new is
   * allowed. Once the order is known it is read; until then the same request is re-sent.
   */
  async function checkAgain() {
    if (unconfirmed === null || submitting) return;
    setSubmitError(null);
    const { orderId, cardId } = unconfirmed;
    if (orderId === null) {
      if (cardId === null) await submitCompOrder();
      else await placeCardOrder(cardId);
      return;
    }
    setSubmitting(true);
    try {
      finishOrder(await getMemberOrder(contactId, orderId), cardId);
    } catch {
      // A failed read settles nothing: the attempt stays unconfirmed under its key.
      setUnconfirmed((current) => (current === null ? null : { ...current, readFailed: true }));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Tokenize the entered card and SAVE it, returning the saved `cardId` the order is
   * then charged to. The nonce is single-use and local — never stored or logged, and no
   * PAN or CVV ever enters this component. `locationId` binds the card to the location
   * the admin is working at, which is what keeps a later sale off the mismatch `422`.
   *
   * A failed save throws, so the caller issues **no order at all**. A save that answers
   * without a usable id is the same situation: the nonce is already spent, so there is
   * nothing left to charge and nothing is attempted.
   */
  async function saveEnteredCard(): Promise<string> {
    if (tokenizeRef.current === null) throw new Error('Card entry is not ready.');
    const cardToken = await tokenizeRef.current();
    // `reused: true` is a SUCCESS — the member simply already had this card on file.
    const saved = await saveMemberCard(contactId, cardToken, locationId);
    if (saved.cardId.length === 0) {
      throw new Error('The card was saved but could not be identified, so nothing was charged.');
    }
    return saved.cardId;
  }

  /**
   * The recovery from either connection `422`: take the card again, here. The saved-card
   * selection is dropped (revealing the card field), the error clears, and the attempt
   * key is dropped so the next order carries a FRESH `idempotencyKey` — the credential
   * changed, so reusing the failed attempt's key would be wrong. The plan selection, the
   * quote and the dialog are untouched; the member keeps both cards.
   */
  function takeCardAgain() {
    selectCardOnFile(null);
  }

  async function settleCardOrder(order: MembershipOrder, cardId: string) {
    let terminal: MembershipOrder;
    try {
      terminal = order.status === 'PENDING' ? await settlePendingOrder(order) : order;
    } catch (error) {
      // DNKADM-0467: the order exists, so whatever failed after it (an action this console
      // cannot drive, a failed poll) leaves it unconfirmed under its key; "Check again"
      // then reads it by id. An unsupported action keeps its explicit copy.
      setPaymentNote(null);
      setUnconfirmed({
        message:
          error instanceof UnsupportedPaymentActionError
            ? toOrderErrorView(error).message
            : unconfirmedMessage('card'),
        orderId: order.orderId,
        cardId,
      });
      return;
    }
    finishOrder(terminal, cardId);
  }

  /** A comp (`cardId` null) or card order the server answered with. */
  function finishOrder(terminal: MembershipOrder, cardId: string | null) {
    if (terminal.status === 'COMPLETED') {
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      setPaymentNote(null);
      setReceipt(terminal);
      onCompleted(terminal);
      return;
    }
    const message = cardId === null ? compOrderMessage(terminal) : terminalOrderMessage(terminal);
    // Not settled, so not terminal (DNKADM-0467): nothing was applied yet, and the
    // attempt keeps its key until "Check again" reads it settled. NEEDS_REVIEW is held
    // the same way (DNKADM-0472): the card was charged and nothing applied, so a fresh
    // key here would let the desk charge again.
    if (terminal.status === 'PENDING' || isNeedsReview(terminal.status)) {
      setUnconfirmed({ message, orderId: terminal.orderId, cardId, needsReview: isNeedsReview(terminal.status) });
      return;
    }
    // A terminal read ends the attempt.
    attemptKeyRef.current = null;
    setUnconfirmed(null);
    setSubmitError({ retryable: false, message });
  }

  async function settlePendingOrder(order: MembershipOrder): Promise<MembershipOrder> {
    setPaymentNote(paymentActionNote(order.paymentAction));
    await drivePaymentAction(order.paymentAction);
    if (order.orderId === null) return order;
    for (let i = 0; i < ORDER_MAX_POLLS; i += 1) {
      await wait(ORDER_POLL_INTERVAL_MS);
      const current = await getMemberOrder(contactId, order.orderId);
      if (current.status !== 'PENDING') return current;
    }
    return order;
  }

  const compPinInputProps = useManagerPinInputProps(compPin, (pin) => {
    // A PIN typed for "Check again" belongs to the unconfirmed attempt
    // and must not start a new one (DNKADM-0467).
    if (unconfirmed === null) clearAttemptForEdit();
    setCompPinError(null);
    setCompPin(pin);
  });

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle
        id={titleId}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="workspace_premium" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Buy membership</Typography>
          <Typography variant="body2">Comp or card order</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {!locationReady && (
            <Alert
              severity="warning"
              action={
                location.status === 'error' ? (
                  <Button color="inherit" size="small" onClick={location.retry}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {locationMessage(location.status)}
            </Alert>
          )}

          {plansState.status === 'error' && (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button color="inherit" size="small" onClick={loadPlans}>
                  Retry
                </Button>
              }
            >
              {plansState.message}
            </Alert>
          )}

          {boundPlanMissing && (
            <Alert severity="error" role="alert">
              That membership plan is no longer available. Close this dialog and reopen Buy
              membership to check a current plan.
            </Alert>
          )}

          {boundPlanId !== null ? (
            /* Locked to the pre-flighted plan (DNKADM-0231) — the PLAN identity stays
               read-only; the distinct membership-rate control below is editable. */
            <TextField
              id={planId}
              label="Plan"
              fullWidth
              size="small"
              value={selectedPlan?.name ?? ''}
              slotProps={{ input: { readOnly: true } }}
              helperText={
                selectedPlan
                  ? `${formatMoney(selectedPlan.price)} · ${selectedPlan.termDays} days`
                  : ' '
              }
            />
          ) : (
            <TextField
              id={planId}
              label="Plan"
              select
              required
              fullWidth
              size="small"
              value={
                plansState.plans.some((plan) => plan.id === selectedPlanId) ? selectedPlanId : ''
              }
              onChange={(event) => {
                clearAttemptForEdit();
                setMembershipRateEdit(undefined);
                storeAuthoritativeCurrency(null);
                negotiatedPrice.reset();
                setSelectedPlanId(event.target.value);
              }}
              disabled={locked || receipt !== null || plansState.status !== 'ready'}
              helperText={
                selectedPlan
                  ? `${formatMoney(selectedPlan.price)} · ${selectedPlan.termDays} days`
                  : ' '
              }
            >
              {plansState.status === 'loading' && (
                <MenuItem value="" disabled>
                  Loading plans...
                </MenuItem>
              )}
              {plansState.status === 'ready' && plansState.plans.length === 0 && (
                <MenuItem value="" disabled>
                  No membership plans available
                </MenuItem>
              )}
              {groupedPlanOptions(plansState.plans)}
            </TextField>
          )}

          {paymentMode === 'card' && selectedPlan !== null && (
            <TextField
              label="Membership rate"
              required
              fullWidth
              size="small"
              value={membershipRateEdit ?? majorPriceInput(selectedPlan.price)}
              onChange={(event) => {
                clearNegotiatedAttemptForEdit();
                negotiatedPrice.invalidate();
                setMembershipRateEdit(event.target.value);
              }}
              disabled={
                locked || receipt !== null || authoritativeCurrency === null
              }
              error={negotiatedRateInvalid}
              helperText={
                negotiatedRateInvalid
                  ? 'Enter a valid rate greater than 0.'
                  : authoritativeCurrency === null
                    ? 'Waiting for the pricing currency.'
                    : `Pre-tax rate in ${authoritativeCurrency}`
              }
              slotProps={{ htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }}
            />
          )}

          {paymentMode === 'card' && hasNegotiatedRate && receipt === null && (
            <>
              <Alert severity="warning">{NEGOTIATED_MEMBERSHIP_COPY}</Alert>
              <NegotiatedPricePinPrompt
                draftPin={negotiatedPrice.draftPin}
                disabled={locked}
                onDraftChange={(value) => {
                  clearNegotiatedAttemptForEdit();
                  negotiatedPrice.setDraftPin(value);
                }}
                onApply={() => {
                  clearNegotiatedAttemptForEdit();
                  negotiatedPrice.apply();
                }}
              />
            </>
          )}

          <ToggleButtonGroup
            exclusive
            size="small"
            value={paymentMode}
            onChange={(_event, value: PaymentMode | null) => {
              if (value === null || value === paymentMode || locked || receipt !== null) return;
              clearAttemptForEdit();
              if (value === 'comp') {
                setMembershipRateEdit(undefined);
                storeAuthoritativeCurrency(null);
                negotiatedPrice.reset();
              }
              setPaymentMode(value);
            }}
            aria-label="Payment mode"
            sx={{ alignSelf: 'flex-start' }}
          >
            <ToggleButton value="card" aria-label="Pay card">
              Pay card
            </ToggleButton>
            {compChoiceShown(compPinGate, isStaffTier) && (
              <ToggleButton value="comp" aria-label="Comp">
                Comp
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          {paymentMode === 'comp' && (
            <>
              <TextField
                id={termId}
                label="Comp term"
                select
                required
                fullWidth
                size="small"
                value={String(termMonths)}
                onChange={(event) => {
                  clearAttemptForEdit();
                  setTermMonths(Number(event.target.value));
                }}
                disabled={locked || receipt !== null}
                helperText="Months of active membership to grant."
              >
                {COMP_TERMS.map((months) => (
                  <MenuItem key={months} value={months}>
                    {months} {months === 1 ? 'month' : 'months'}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                id={reasonId}
                label="Reason (optional)"
                fullWidth
                size="small"
                value={reason}
                onChange={(event) => {
                  clearAttemptForEdit();
                  setReason(event.target.value);
                }}
                disabled={locked || receipt !== null}
              />

              {compPinFieldShown && (
                <TextField
                  label="Manager PIN to comp"
                  required={compPinFieldRequired}
                  fullWidth
                  size="small"
                  {...compPinInputProps}
                  disabled={submitting || receipt !== null}
                  error={compPinError !== null}
                  helperText={
                    compPinError ??
                    (compPinFieldRequired
                      ? isStaffTier
                        ? 'Staff need a manager to enter the PIN for this.'
                        : ' '
                      : 'Leave blank if this location has no manager PIN.')
                  }
                />
              )}

              {compPinGate.status === 'loading' && (
                <Typography variant="caption" sx={{ color: inkAlpha(theme, 0.62) }}>
                  Checking manager PIN requirement...
                </Typography>
              )}
            </>
          )}

          {paymentMode === 'card' && (
            <CardCheckoutPanel
              quoteState={quoteState}
              quoteRetry={loadQuote}
              contactId={contactId}
              cardOnFileId={cardOnFileId}
              onSelectCardOnFile={selectCardOnFile}
              tenantSlug={tenantSlug}
              locationRef={location.selectedId ?? ''}
              tenantReady={tenantReady}
              locationReady={locationReady}
              submitting={locked}
              receiptReady={receipt !== null}
              billingStart={billingStart}
              onBillingStartChange={(next) => {
                billingStartTouchedRef.current = true;
                setBillingStart(next);
              }}
              onReady={(tokenize) => {
                tokenizeRef.current = tokenize;
                setCardReady(true);
              }}
              onError={() => {
                tokenizeRef.current = null;
                setCardReady(false);
              }}
            />
          )}

          {paymentNote && <Alert severity="info">{paymentNote}</Alert>}

          {unconfirmed !== null && (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void checkAgain()}
                  disabled={submitting}
                >
                  Check again
                </Button>
              }
            >
              {unconfirmed.message}
              {unconfirmed.readFailed === true && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {CHECK_AGAIN_FAILED}
                </Typography>
              )}
            </Alert>
          )}

          {submitError?.openOrder !== undefined && (
            <OrderNeedsAttentionAlert
              openOrder={submitError.openOrder}
              contactId={contactId}
              onLeave={close}
            />
          )}
          {submitError && submitError.openOrder === undefined && (
            <Alert
              severity="error"
              role="alert"
              action={
                /* A connection `422` is NOT retryable as-is: re-sending the same
                   `cardId` cannot succeed, so the only action offered is a new card. */
                submitError.recovery === 'new-card' ? (
                  <Button color="inherit" size="small" onClick={takeCardAgain}>
                    Take the card again
                  </Button>
                ) : submitError.retryable ? (
                  <Button color="inherit" size="small" onClick={() => void submit()}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {submitError.message}
            </Alert>
          )}

          {reversalError && (
            <Alert severity="error" role="alert">
              {reversalError}
            </Alert>
          )}

          {receipt && (
            <Box
              role="status"
              sx={{
                border: `1px solid ${t.line}`,
                borderRadius: 1,
                p: 1.5,
                bgcolor: t.surface,
              }}
            >
              <Stack spacing={1}>
                {reversalMessage && <Alert severity="success">{reversalMessage}</Alert>}
                <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>Order</Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>
                    {receipt.orderNumber ?? receipt.orderId ?? PLACEHOLDER}
                  </Typography>
                </Stack>
                <OrderStatusRow status={receipt.status} />
                <Divider />
                <ReceiptRow
                  label="Plan"
                  value={receipt.lines[0]?.tier ?? selectedPlan?.name ?? PLACEHOLDER}
                />
                <ReceiptRow
                  label={paymentMode === 'comp' ? 'Term' : 'Payment'}
                  value={
                    paymentMode === 'comp'
                      ? `${termMonths} ${termMonths === 1 ? 'month' : 'months'}`
                      : 'Card'
                  }
                />
                <ReceiptRow
                  label="Total"
                  value={receiptTotalLabel(receipt)}
                  strong
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={submitting || reversing !== null}
          sx={{ color: t.text2 }}
        >
          {receipt ? 'Close' : 'Cancel'}
        </Button>
        {receipt?.status === 'COMPLETED' && (
          <>
            {/* DNKADM-0436 V-15: only the verb the payment mode permits (the DNKADM-0305
                ledger rule); an unresolved mode offers both and the server stays the authority. */}
            {receiptReversalVerbs(receipt).includes('refund') && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<Icon name="undo" />}
                onClick={() => void reverseReceipt('refund')}
                disabled={reversing !== null}
              >
                {reversing === 'refund' ? 'Refunding...' : 'Refund'}
              </Button>
            )}
            {receiptReversalVerbs(receipt).includes('void') && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<Icon name="block" />}
                onClick={() => void reverseReceipt('void')}
                disabled={reversing !== null}
              >
                {reversing === 'void' ? 'Voiding...' : 'Void'}
              </Button>
            )}
          </>
        )}
        {!receipt && (
          <Button
            variant="contained"
            startIcon={<Icon name="check" />}
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {submitButtonLabel(paymentMode, submitting, quoteState, billingStart)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * The reversal verbs a COMPLETED receipt may offer (DNKADM-0436 V-15), by the same rule the
 * ledger row uses (DNKADM-0305), which keys on ONE order-level mode: Void only when every
 * recognised payment mode is `comp` (no money taken); Refund only when every recognised mode is
 * `card` / `external`. A mixed or unrecognised set offers both, because the server
 * (`OrderReversalService.requireReversible`) is the authority on which verb it accepts.
 */
function receiptReversalVerbs(order: {
  payments: ReadonlyArray<{ mode: string | null }>;
}): ReadonlyArray<'refund' | 'void'> {
  const modes = order.payments
    .map((p) => p.mode?.trim().toLowerCase() ?? '')
    .filter((m) => m === 'card' || m === 'external' || m === 'comp');
  if (modes.length === 0) return ['refund', 'void'];
  if (modes.every((m) => m === 'comp')) return ['void'];
  if (modes.every((m) => m === 'card' || m === 'external')) return ['refund'];
  return ['refund', 'void'];
}

function ReceiptRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Stack>
  );
}

function CardCheckoutPanel({
  quoteState,
  quoteRetry,
  contactId,
  cardOnFileId,
  onSelectCardOnFile,
  tenantSlug,
  locationRef,
  tenantReady,
  locationReady,
  submitting,
  receiptReady,
  billingStart,
  onBillingStartChange,
  onReady,
  onError,
}: {
  quoteState: QuoteState;
  quoteRetry: () => void;
  contactId: string;
  cardOnFileId: string | null;
  onSelectCardOnFile: (cardId: string | null) => void;
  tenantSlug: string;
  locationRef: string;
  tenantReady: boolean;
  locationReady: boolean;
  submitting: boolean;
  receiptReady: boolean;
  billingStart: BillingStartChoice;
  onBillingStartChange: (v: BillingStartChoice) => void;
  onReady: (tokenize: TokenizeFn) => void;
  onError: (error: Error) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);

  // DNKADM-0374: does the quote carry an available billing anchor?
  const billingAnchor =
    quoteState.status === 'ready' ? quoteState.quote.billingAnchor : undefined;
  const anchorAvailable = billingAnchor?.available === true;
  // Defer-only: anchor available but prorate absent (below-floor).
  const deferOnly = anchorAvailable && billingAnchor?.prorate === undefined;
  // Show radios: anchor available.
  const showBillingChoice = anchorAvailable;

  return (
    <Stack spacing={1.5}>
      {!tenantReady && (
        <Alert severity="warning">
          Tenant payment context is unavailable. Sign in again before charging a card.
        </Alert>
      )}
      {quoteState.status === 'loading' && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading pricing quote...</Typography>
        </Stack>
      )}
      {quoteState.status === 'blocked' && (
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
          Fix the negotiated membership rate to re-price this membership.
        </Typography>
      )}
      {quoteState.status === 'awaiting-override-pin' && (
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
          Enter the override PIN and apply the negotiated price to re-price this membership.
        </Typography>
      )}
      {quoteState.status === 'error' && (
        <Alert
          severity="error"
          role="alert"
          action={
            quoteState.retryable ? (
              <Button color="inherit" size="small" onClick={quoteRetry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {quoteState.message}
        </Alert>
      )}
      {quoteState.status === 'ready' && (
        <>
          {/* DNKADM-0380 U2: billing start — two option cards when both are offered, or a single
              statement (not a one-item chooser) when the anchor is below-floor defer-only. Every
              figure is the server quote's; nothing is computed or rounded here. */}
          {showBillingChoice && billingAnchor && (
            deferOnly || billingAnchor.prorate === undefined ? (
              <BillingStartStatement
                startOn={billingAnchor.startOn}
                currency={quoteState.quote.currency ?? undefined}
              />
            ) : (
              <BillingStartOptionCards
                value={billingStart}
                onChange={onBillingStartChange}
                disabled={submitting || receiptReady}
                options={[
                  {
                    value: 'defer',
                    name: 'Defer',
                    dueToday: formatMoney(0, quoteState.quote.currency ?? undefined),
                    firstFullCharge: formatApiDate(billingAnchor.startOn),
                    creditsNow: 'One-time credits (recurring credits land with the first charge)',
                  },
                  {
                    value: 'pay',
                    name: 'Pay through',
                    dueToday:
                      billingAnchor.prorate.tax !== null && billingAnchor.prorate.total !== null
                        ? formatMoney(
                            billingAnchor.prorate.total,
                            billingAnchor.prorate.currency ??
                              quoteState.quote.currency ??
                              undefined,
                          )
                        : 'Tax calculated at checkout',
                    firstFullCharge: formatApiDate(billingAnchor.startOn),
                    creditsNow: describeCredits(billingAnchor.prorate.credits),
                  },
                ]}
              />
            )
          )}

          {/* Totals box */}
          <Box
            sx={{
              border: `1px solid ${t.line}`,
              borderRadius: 1,
              bgcolor: t.surface,
              p: 1.5,
            }}
          >
            <Stack spacing={0.75}>
              {showBillingChoice ? (
                /* DNKADM-0374 anchored path: Full plan price (informational) + Due today */
                <>
                  <ReceiptRow
                    label="Full plan price"
                    value={formatMoney(
                      quoteState.quote.total ?? quoteState.quote.subtotal,
                      quoteState.quote.currency ?? undefined,
                    )}
                  />
                  <Divider />
                  {billingStart === 'defer' || deferOnly ? (
                    <ReceiptRow label="Due today" value={formatMoney(0, quoteState.quote.currency ?? undefined)} strong />
                  ) : billingAnchor?.prorate?.tax !== null &&
                    billingAnchor?.prorate?.total !== null &&
                    billingAnchor?.prorate !== undefined ? (
                    <ReceiptRow
                      label="Due today"
                      value={formatMoney(
                        billingAnchor.prorate.total!,
                        billingAnchor.prorate.currency ??
                          quoteState.quote.currency ??
                          undefined,
                      )}
                      strong
                    />
                  ) : (
                    <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>
                      Tax calculated at checkout
                    </Typography>
                  )}
                </>
              ) : (
                /* Unanchored path: existing subtotal/tax/total trichotomy */
                <>
                  <ReceiptRow
                    label="Subtotal"
                    value={formatMoney(
                      quoteState.quote.subtotal,
                      quoteState.quote.currency ?? undefined,
                    )}
                  />
                  {quoteState.quote.tax !== null && quoteState.quote.total !== null ? (
                    <>
                      <ReceiptRow
                        label="Tax"
                        value={formatMoney(
                          quoteState.quote.tax,
                          quoteState.quote.currency ?? undefined,
                        )}
                      />
                      <Divider />
                      <ReceiptRow
                        label="Total due"
                        value={formatMoney(
                          quoteState.quote.total,
                          quoteState.quote.currency ?? undefined,
                        )}
                        strong
                      />
                    </>
                  ) : (
                    /* DNKADM-0241: deferred tax — never render as $0.00 */
                    <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>
                      Tax calculated at checkout
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </Box>
          {locationReady && tenantReady ? (
            <>
              {/* The shared chooser (DNKADM-0253) — mounted only while this dialog is
                  open in card mode, so its list is never cached beyond a sale. */}
              <CardOnFileChooser
                contactId={contactId}
                value={cardOnFileId}
                onChange={onSelectCardOnFile}
                disabled={submitting || receiptReady}
              />
              {/* Nothing to tokenize once a card on file is chosen. */}
              {cardOnFileId === null && (
                <CardPaymentField
                  tenantSlug={tenantSlug}
                  locationRef={locationRef}
                  disabled={submitting || receiptReady}
                  onReady={onReady}
                  onError={onError}
                />
              )}
            </>
          ) : (
            <Alert severity="warning">Select a location before charging the card.</Alert>
          )}
        </>
      )}
    </Stack>
  );
}

/** One billing-start option as the card renders it — every string is server-sourced. */
type BillingStartOption = {
  value: BillingStartChoice;
  name: string;
  dueToday: string;
  firstFullCharge: string;
  /** The credits that land now, or null when the quote lists none. */
  creditsNow: string | null;
};

/**
 * DNKADM-0380 U2 — the billing-start chooser as two selectable option cards, side by side on
 * desktop and stacked on narrow widths. The selected card takes the same treatment as the
 * Pay card / Comp toggle above it (brand fill, brand ink) so the dialog has one selection idiom.
 *
 * Radiogroup semantics are kept by hand because the cards are not native radios: the container
 * is `role="radiogroup"`, each card is `role="radio"` with `aria-checked`, the selected card is
 * the group's single tab stop (roving tabindex), and the arrow keys move selection AND focus
 * between the cards. Name comes from the option title; the facts are the description.
 */
function BillingStartOptionCards({
  value,
  onChange,
  disabled,
  options,
}: {
  value: BillingStartChoice;
  onChange: (next: BillingStartChoice) => void;
  disabled: boolean;
  options: readonly BillingStartOption[];
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const moveSelection = (from: number, delta: number) => {
    const next = (from + delta + options.length) % options.length;
    const option = options[next];
    if (option === undefined) return;
    onChange(option.value);
    cardRefs.current[next]?.focus();
  };

  return (
    <Stack
      role="radiogroup"
      aria-label="Billing start"
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ alignItems: 'stretch' }}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        const nameId = `${ids}-${option.value}-name`;
        const descriptionId = `${ids}-${option.value}-description`;
        return (
          <ButtonBase
            key={option.value}
            ref={(element) => {
              cardRefs.current[index] = element;
            }}
            role="radio"
            aria-checked={selected}
            aria-labelledby={nameId}
            aria-describedby={descriptionId}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(index, 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(index, -1);
              }
            }}
            sx={{
              flex: 1,
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              textAlign: 'left',
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${selected ? t.brand : t.lineStrong}`,
              bgcolor: selected ? t.brand : t.surface,
              color: selected ? t.brandInk : 'inherit',
              transition: theme.transitions.create(['background-color', 'border-color'], {
                duration: theme.transitions.duration.shortest,
              }),
              '&:hover': { bgcolor: selected ? t.brandDark : inkAlpha(theme, 0.05) },
              '&.Mui-focusVisible': { outline: `2px solid ${t.brand}`, outlineOffset: 2 },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
          >
            <Stack spacing={0.5} sx={{ width: '100%' }}>
              <Typography id={nameId} sx={{ fontSize: 13.5, fontWeight: 600, color: 'inherit' }}>
                {option.name}
              </Typography>
              <Stack
                id={descriptionId}
                spacing={0.25}
                sx={{ color: selected ? 'inherit' : inkAlpha(theme, 0.62) }}
              >
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  Due today: {option.dueToday}
                </Typography>
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  First full charge: {option.firstFullCharge}
                </Typography>
                {option.creditsNow !== null && (
                  <Typography variant="body2" sx={{ color: 'inherit' }}>
                    Credits now: {option.creditsNow}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}

/**
 * DNKADM-0380 U2 — the below-floor (defer-only) case rendered as a statement of what will
 * happen, not as a chooser with one option. Same facts as the Defer card.
 */
function BillingStartStatement({
  startOn,
  currency,
}: {
  startOn: string;
  currency: string | undefined;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <Box
      sx={{
        border: `1px solid ${t.line}`,
        borderRadius: 1,
        bgcolor: t.surface,
        p: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
        Defer — {formatMoney(0, currency)} today
      </Typography>
      <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
        Member is active today. First charge of the full plan on {formatApiDate(startOn)}.
        Recurring credits land then. One-time credits land now.
      </Typography>
    </Box>
  );
}

/**
 * The quote's prorated credits as one line ("2 hours Bay time, 1 Guest pass"), or null when the
 * quote lists none. Same filter and join as the DNKADM-0374 radio label used.
 */
function describeCredits(
  credits: ReadonlyArray<{ amount: number; unit: string | null; bucketName: string | null }>,
): string | null {
  const parts = credits
    .filter((c) => c.bucketName !== null || c.unit !== null)
    .map((c) => [String(c.amount), c.unit, c.bucketName].filter(Boolean).join(' '));
  return parts.length > 0 ? parts.join(', ') : null;
}

function locationMessage(status: string): string {
  if (status === 'loading') return 'Loading locations before creating the order.';
  if (status === 'error') return 'Locations could not be loaded. Retry before creating the order.';
  return 'Select a location before creating the order.';
}

/**
 * The plan picker's options grouped under two headings (DNKADM-0381 U2): the plans members
 * can buy, then the staff-only plans. Both groups are selectable — a staff-only plan sells
 * exactly like any other. A group with no plans renders nothing, not even its heading. A flat
 * array (never fragments): MUI's Select reads its options from its direct children, and a
 * heading carries no `value` (and no tabindex), so it is never a selection.
 */
function groupedPlanOptions(plans: MembershipPlan[]): ReactNode[] {
  const groups = [
    { key: 'members', heading: 'Members and staff', plans: plans.filter((p) => !p.staffOnly) },
    { key: 'staff', heading: 'Staff only', plans: plans.filter((p) => p.staffOnly) },
  ];
  const nodes: ReactNode[] = [];
  for (const group of groups) {
    if (group.plans.length === 0) continue;
    nodes.push(
      <ListSubheader key={`heading-${group.key}`} disableSticky>
        {group.heading}
      </ListSubheader>,
    );
    for (const plan of group.plans) {
      nodes.push(
        <MenuItem key={plan.id} value={plan.id}>
          {plan.name} · {formatMoney(plan.price)}
        </MenuItem>,
      );
    }
  }
  return nodes;
}

function formatMoney(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
}

function submitButtonLabel(
  mode: PaymentMode,
  submitting: boolean,
  quoteState: QuoteState,
  billingStart: BillingStartChoice,
): string {
  if (submitting) return mode === 'card' ? 'Charging...' : 'Creating...';
  if (mode !== 'card') return 'Create comp order';
  if (quoteState.status !== 'ready') return 'Charge card';
  const { quote } = quoteState;
  const anchor = quote.billingAnchor;
  // DNKADM-0374: anchored card — defer or pay-through button labels.
  if (anchor?.available === true) {
    if (billingStart === 'defer' || anchor.prorate === undefined) {
      return 'Start membership · $0.00 today';
    }
    // Pay-through: use stub total if present; else "Charge card" (null tax).
    if (anchor.prorate.total !== null) {
      return `Charge ${formatMoney(
        anchor.prorate.total,
        anchor.prorate.currency ?? quote.currency ?? undefined,
      )}`;
    }
    return 'Charge card';
  }
  // Unanchored / available === false: existing logic.
  // DNKADM-0241: deferred-tax quote has no total → "Charge card".
  if (quote.total !== null) {
    return `Charge ${formatMoney(quote.total, quote.currency ?? undefined)}`;
  }
  return 'Charge card';
}

function quoteRequestMatches(
  ready: Extract<QuoteState, { status: 'ready' }>,
  selectedPlanNumber: number,
  negotiatedRateMinor: number | null,
  overrideAuth: { pin: string } | null,
): boolean {
  return (
    ready.requestLine.refId === selectedPlanNumber &&
    (ready.requestLine.overrideBase ?? null) === negotiatedRateMinor &&
    ready.requestAuth?.pin === overrideAuth?.pin
  );
}

function receiptTotalLabel(order: MembershipOrder): string {
  if (order.grandTotal === 0 && order.compValue !== null && order.compValue > 0) {
    return `Comped (${formatMoney(order.compValue, order.currency ?? undefined)} value)`;
  }
  return formatMoney(order.grandTotal, order.currency ?? undefined);
}

function reversalSuccessMessage(order: MembershipOrder, action: 'refund' | 'void'): string {
  const label = order.orderNumber ?? order.orderId ?? '';
  if (order.status === 'REFUNDED') {
    return `Order ${label} refunded. Receipt refreshed.`.trim();
  }
  if (order.status === 'VOIDED') {
    return `Order ${label} voided. Receipt refreshed.`.trim();
  }
  const actionLabel = action === 'refund' ? 'refund' : 'void';
  return `Order ${label} ${actionLabel} requested. Receipt refreshed.`.trim();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function compOrderMessage(order: MembershipOrder): string {
  return unsettledOrderMessage(order.status, order.orderNumber ?? order.orderId ?? '', {
    pending: 'Order {order} is pending. No membership changes were applied yet.',
    failed: 'Order {order} failed. No membership changes were applied.',
  }).trim();
}

function terminalOrderMessage(order: MembershipOrder): string {
  const label = order.orderNumber ?? order.orderId ?? '';
  return unsettledOrderMessage(order.status, label, {
    pending: 'Order {order} is still pending. No membership changes were applied yet.',
    failed: 'Card payment declined. Order {order} failed. No membership changes were applied.',
  });
}

function paymentActionNote(action: MembershipOrder['paymentAction']): string {
  if (action === null) return 'Waiting for the card payment to finish...';
  const kind = actionKind(action);
  const redirectUrl = asActionText(action['redirectUrl'] ?? action['url']);
  if (redirectUrl !== null || kind?.includes('redirect')) {
    return 'Card authentication requires a redirect. Waiting for payment confirmation...';
  }
  return 'Card authentication is required. Waiting for payment confirmation...';
}

async function drivePaymentAction(action: MembershipOrder['paymentAction']): Promise<void> {
  if (action === null) return;
  const kind = actionKind(action);
  const redirectUrl = asActionText(action['redirectUrl'] ?? action['url']);
  if (
    redirectUrl !== null &&
    (kind === null || kind.includes('redirect') || kind.includes('requires_redirect'))
  ) {
    window.location.assign(redirectUrl);
    return;
  }

  const clientAction = asActionRecord(action['clientAction']);
  if (clientAction !== null) {
    const nestedRedirectUrl = asActionText(clientAction['redirectUrl'] ?? clientAction['url']);
    if (nestedRedirectUrl !== null) {
      window.location.assign(nestedRedirectUrl);
      return;
    }
    await getPaymentActionDriver()(clientAction);
    return;
  }

  if (kind?.includes('requires_action') || kind?.includes('requires_redirect')) {
    throw new UnsupportedPaymentActionError();
  }
}

function actionKind(action: Record<string, unknown>): string | null {
  return (
    asActionText(
      action['type'] ?? action['kind'] ?? action['action'] ?? action['status'] ?? action['state'],
    )?.toLowerCase() ?? null
  );
}

function asActionText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 ? text : null;
}

function asActionRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return { ...(value as Record<string, unknown>) };
}

function apiEnvelopeCode(error: ApiError): string | null {
  const code = error.details?.['code'];
  return typeof code === 'string' ? code : null;
}

function toOrderErrorView(
  error: unknown,
  fallbackMessage = 'The order could not be created.',
): SubmitError {
  if (error instanceof CardTokenizationError) {
    return {
      retryable: false,
      message: error.validation
        ? 'Finish entering the card before charging.'
        : 'The card could not be tokenized. Ask the member to check the card and try again.',
    };
  }
  if (error instanceof UnsupportedPaymentActionError) {
    return {
      retryable: false,
      message:
        'Card authentication requires an unsupported action. Refresh this order or contact support before trying again.',
    };
  }
  const overrideView = negotiatedPriceErrorView(error);
  if (overrideView !== null) return overrideView;
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const suffix = requestId ? ` Request ID: ${requestId}` : '';
  if (error instanceof ApiError) {
    const message = serverMessage(error);
    const envelopeCode = apiEnvelopeCode(error);
    if (
      error.status === 402 ||
      envelopeCode === 'PAYMENT_DECLINED' ||
      envelopeCode === 'CARD_DECLINED'
    ) {
      // DNKAPI-0676: a known decline reason replaces the plain decline line; OTHER/unknown keep it.
      // The membership-plan path settles through the subscribe engine, which sends no reason
      // today, so this keeps the plain line until dink-api adds one.
      const reasonLine = declineReasonLineFromError(error);
      return {
        retryable: false,
        message: `${reasonLine ?? message ?? 'Card payment declined. Ask the member for another card.'}${suffix}`,
      };
    }
    // The saved card cannot be charged for THIS membership (DNKADM-0253). Branch on the
    // envelope code only, show the server's own message, and offer the one thing that
    // can work — a new card. Never a plain Retry: the same `cardId` would fail again.
    if (error.status === 422 && envelopeCode === SAVED_CARD_CONNECTION_CODES.mismatch) {
      return {
        retryable: false,
        recovery: 'new-card',
        message: `${
          message ??
          'That card was saved at a different location and cannot be charged for this membership.'
        }${suffix}`,
      };
    }
    if (error.status === 422 && envelopeCode === SAVED_CARD_CONNECTION_CODES.unavailable) {
      return {
        retryable: false,
        recovery: 'new-card',
        message: `${
          message ?? 'That card can no longer be charged. Take the card again to continue.'
        }${suffix}`,
      };
    }
    if (
      (error.status === 422 || error.status === 403) &&
      envelopeCode === 'CONNECTION_NOT_AUTHORIZED'
    ) {
      const reconnect = 'Reconnect Square before accepting card payments for this location.';
      return {
        retryable: false,
        message: `${message ? `${message} ${reconnect}` : reconnect}${suffix}`,
      };
    }
    switch (error.code) {
      case 'SESSION_EXPIRED':
        return {
          retryable: false,
          message: `Your session expired. Please sign in again to continue.${suffix}`,
        };
      case 'FORBIDDEN':
      case 'NOT_FOUND':
      case 'VALIDATION':
      case 'CONFLICT':
      case 'UNKNOWN':
      case 'RATE_LIMITED':
      case 'NETWORK_ERROR':
        return {
          retryable: error.retryable === true,
          message: `${message ?? fallbackMessage}${suffix}`,
        };
    }
  }
  return { retryable: false, message: fallbackMessage };
}

const PLACEHOLDER = '-';
